import React from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Divider
} from '@mui/material';
import { DrfData, DadosDemonstrativo, CfopDePara } from '../../Pages/AnalisesDrf/types';

interface DrfPrintComponentProps {
    data: DrfData;
    title: string;
}

const DrfPrintComponent: React.FC<DrfPrintComponentProps> = ({ data, title }) => {
    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value);
    };

    const formatPercentage = (value: number, total: number) => {
        if (total === 0) return '0,00%';
        return ((value / total) * 100).toFixed(2).replace('.', ',') + '%';
    };

    const formatDate = (dateString: string) => {
        if (!dateString || dateString === 'N/A') return 'N/A';
        return dateString;
    };

    const renderTableRows = () => {
        const rows: React.ReactElement[] = [];

        // Adiciona os dados do demonstrativo
        (data?.dados_demonstrativo || []).forEach((grupo: DadosDemonstrativo, grupoIndex: number) => {
            
            // Tratamento especial para grupos calculados (adoptedSon e childfree)
            if (['adoptedSon', 'childfree'].includes(grupo.grouping_drf_data?.type || '')) {
                const valorTotalGrupoEspecial = grupo.grouping_drf_data?.value || 'R$ 0,00';
                const percentualGrupoEspecial = grupo.grouping_drf_data?.percentual || '0,00%';
                
                // Extrai o valor numérico para verificar se é negativo
                const valorNumerico = parseFloat(valorTotalGrupoEspecial.replace(/[R$\s.]/g, '').replace(',', '.')) || 0;
                
                rows.push(
                    <TableRow key={`grupo-${grupoIndex}`}>
                        <TableCell 
                            sx={{ 
                                fontWeight: 'bold', 
                                fontSize: '12px',
                                paddingLeft: '8px',
                                borderBottom: '1px solid #ddd',
                                backgroundColor: '#e3f2fd',
                                color: valorNumerico < 0 ? '#d32f2f' : 'inherit'
                            }}
                        >
                            {grupo.grouping_drf_data.description}
                        </TableCell>
                        <TableCell 
                            align="right" 
                            sx={{ 
                                fontWeight: 'bold', 
                                fontSize: '12px',
                                borderBottom: '1px solid #ddd',
                                backgroundColor: '#e3f2fd',
                                color: valorNumerico < 0 ? '#d32f2f' : 'inherit'
                            }}
                        >
                            {valorTotalGrupoEspecial}
                        </TableCell>
                        <TableCell 
                            align="right" 
                            sx={{ 
                                fontWeight: 'bold', 
                                fontSize: '12px',
                                borderBottom: '1px solid #ddd',
                                backgroundColor: '#e3f2fd',
                                color: valorNumerico < 0 ? '#d32f2f' : 'inherit'
                            }}
                        >
                            {percentualGrupoEspecial}
                        </TableCell>
                    </TableRow>
                );
                
                // Para grupos 'childfree', não mostra detalhes
                if(grupo.grouping_drf_data?.type === 'childfree') {
                    return;
                }
            }

            // Calcula o valor total do grupo somando todos os cfop_de_para
            const valorTotalGrupo = grupo.cfop_de_para.reduce((sum: number, cfop: CfopDePara) => {
                return sum + parseFloat(cfop.valor_total_c170 || '0');
            }, 0);

            // Linha do grupo principal (grouping_description) - apenas para grupos normais
            if (!['adoptedSon', 'childfree'].includes(grupo.grouping_drf_data?.type || '')) {
                rows.push(
                    <TableRow key={`grupo-${grupoIndex}`}>
                        <TableCell 
                            sx={{ 
                                fontWeight: 'bold', 
                                fontSize: '12px',
                                paddingLeft: '8px',
                                borderBottom: '1px solid #ddd',
                                backgroundColor: '#f5f5f5',
                                color: valorTotalGrupo < 0 ? '#d32f2f' : 'inherit'
                            }}
                        >
                            {grupo.grouping_drf_data.description}
                        </TableCell>
                        <TableCell 
                            align="right" 
                            sx={{ 
                                fontWeight: 'bold', 
                                fontSize: '12px',
                                borderBottom: '1px solid #ddd',
                                backgroundColor: '#f5f5f5',
                                color: valorTotalGrupo < 0 ? '#d32f2f' : 'inherit'
                            }}
                        >
                            {formatCurrency(valorTotalGrupo)}
                        </TableCell>
                        <TableCell 
                            align="right" 
                            sx={{ 
                                fontWeight: 'bold', 
                                fontSize: '12px',
                                borderBottom: '1px solid #ddd',
                                backgroundColor: '#f5f5f5',
                                color: valorTotalGrupo < 0 ? '#d32f2f' : 'inherit'
                            }}
                        >
                            {formatPercentage(valorTotalGrupo, data.receita_total)}
                        </TableCell>
                    </TableRow>
                );
            }

            // Adiciona as operações individuais (cfop_de_para) apenas se houver dados
            if (grupo.cfop_de_para && grupo.cfop_de_para.length > 0) {
                grupo.cfop_de_para.forEach((cfop: CfopDePara, cfopIndex: number) => {
                    const valorCfop = parseFloat(cfop.valor_total_c170 || '0');
                    // Só mostra se o valor não for zero
                    if (valorCfop !== 0) {
                        rows.push(
                            <TableRow key={`cfop-${grupoIndex}-${cfopIndex}`}>
                                <TableCell 
                                    sx={{ 
                                        paddingLeft: '24px', 
                                        fontSize: '11px',
                                        borderBottom: '1px solid #eee',
                                        color: valorCfop < 0 ? '#d32f2f' : 'inherit'
                                    }}
                                >
                                    {cfop.operacao_fechamento_fiscal}
                                </TableCell>
                                <TableCell 
                                    align="right" 
                                    sx={{ 
                                        fontSize: '11px',
                                        borderBottom: '1px solid #eee',
                                        color: valorCfop < 0 ? '#d32f2f' : 'inherit'
                                    }}
                                >
                                    {formatCurrency(valorCfop)}
                                </TableCell>
                                <TableCell 
                                    align="right" 
                                    sx={{ 
                                        fontSize: '11px',
                                        borderBottom: '1px solid #eee',
                                        color: valorCfop < 0 ? '#d32f2f' : 'inherit'
                                    }}
                                >
                                    {formatPercentage(valorCfop, data.receita_total)}
                                </TableCell>
                            </TableRow>
                        );
                    }
                });
            }
        });

        return rows;
    };

    return (
        <Box 
            sx={{ 
                width: '100%', 
                backgroundColor: 'white',
                padding: '20px',
                '@media print': {
                    padding: '10px',
                    margin: 0,
                    boxShadow: 'none'
                }
            }}
        >
            {/* Cabeçalho do Relatório */}
            <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography 
                    variant="h5" 
                    sx={{ 
                        fontWeight: 'bold', 
                        mb: 1,
                        '@media print': {
                            fontSize: '18px'
                        }
                    }}
                >
                    {title}
                </Typography>
            </Box>

            {/* Informações da Empresa */}
            <Box sx={{ mb: 3 }}>
                <Typography 
                    variant="h6" 
                    sx={{ 
                        fontWeight: 'bold', 
                        mb: 1,
                        '@media print': {
                            fontSize: '14px'
                        }
                    }}
                >
                    Informações da Empresa
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            '@media print': {
                                fontSize: '12px'
                            }
                        }}
                    >
                        <strong>Razão Social:</strong> {data.cabecalho.razao_social}
                    </Typography>
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            '@media print': {
                                fontSize: '12px'
                            }
                        }}
                    >
                        <strong>CNPJ:</strong> {data.cabecalho.cnpj}
                    </Typography>
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            '@media print': {
                                fontSize: '12px'
                            }
                        }}
                    >
                        <strong>Período:</strong> {data.cabecalho.periodo}
                    </Typography>
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            '@media print': {
                                fontSize: '12px'
                            }
                        }}
                    >
                        <strong>Data de Geração:</strong> {formatDate(data.cabecalho.data_geracao)}
                    </Typography>
                </Box>
            </Box>

            {/* Tabela Detalhada */}
            <Typography 
                variant="h6" 
                sx={{ 
                    fontWeight: 'bold', 
                    mb: 2,
                    '@media print': {
                        fontSize: '14px'
                    }
                }}
            >
                Detalhamento por Operação
            </Typography>

            <TableContainer 
                component={Paper} 
                sx={{ 
                    '@media print': {
                        boxShadow: 'none',
                        border: '1px solid #ddd'
                    }
                }}
            >
                <Table 
                    size="small" 
                    sx={{ 
                        '@media print': {
                            fontSize: '10px'
                        }
                    }}
                >
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#1976d2' }}>
                            <TableCell 
                                sx={{ 
                                    color: 'white', 
                                    fontWeight: 'bold', 
                                    width: '60%',
                                    '@media print': {
                                        color: '#000',
                                        backgroundColor: '#f0f0f0',
                                        fontSize: '11px'
                                    }
                                }}
                            >
                                Descrição
                            </TableCell>
                            <TableCell 
                                align="right" 
                                sx={{ 
                                    color: 'white', 
                                    fontWeight: 'bold', 
                                    width: '25%',
                                    '@media print': {
                                        color: '#000',
                                        backgroundColor: '#f0f0f0',
                                        fontSize: '11px'
                                    }
                                }}
                            >
                                Valor
                            </TableCell>
                            <TableCell 
                                align="right" 
                                sx={{ 
                                    color: 'white', 
                                    fontWeight: 'bold', 
                                    width: '15%',
                                    '@media print': {
                                        color: '#000',
                                        backgroundColor: '#f0f0f0',
                                        fontSize: '11px'
                                    }
                                }}
                            >
                                % sobre a receita
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {renderTableRows()}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Rodapé */}
            <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Typography 
                    variant="caption" 
                    sx={{ 
                        color: '#666',
                        '@media print': {
                            color: '#000',
                            fontSize: '10px'
                        }
                    }}
                >
                    Relatório gerado em {new Date().toLocaleString('pt-BR')} | Sistema TRIBUNEX
                </Typography>
            </Box>
        </Box>
    );
};

export default DrfPrintComponent;
